import { ProjectCardContent } from "@components/common/project-card/project-card-content";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { BaseProjectProps } from "@custom-types/project-types";
import { generateProjectDetailsRoute } from "@router/router-helper";
import { unstyledLinkSx } from "@styles/common-styles";
import { isProjectProcessingSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  getTrackProjectDateProps,
  trackOpenProjectDetails,
} from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";

interface Props extends BaseCompanyIdProps, BaseProjectProps {
  /** current number of projects displayed */
  numberOfProjects: number;

  /** The field by which the projects are sorted */
  sortBy: FaroCoreApiTypes.ProjectsSortBy;

  /**
   * When `true`, only the thumbnail image is a link to the project details page.
   * The rest of the card stays non-interactive and card-wide hover effects are
   * suppressed so the thumbnail is the sole interactive surface.
   * Used by the map marker popup.
   * @defaults  `false`.
   */
  isThumbnailLink?: boolean;
}

/**
 * Project card component that displays summary information about a project.
 */
export function ProjectCard({
  companyId,
  project,
  numberOfProjects,
  sortBy,
  isThumbnailLink = false,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const isProjectProcessing = useAppSelector(
    isProjectProcessingSelector(project.id)
  );

  const isThumbnailLinkActive = isThumbnailLink && !isProjectProcessing;
  const projectDetailsRoute = generateProjectDetailsRoute(
    companyId,
    project.id
  );

  /** Tracks an "open project details" event in amplitude for the given view mode. */
  function trackOpenDetails(viewMode: "cards" | "map"): void {
    trackOpenProjectDetails({
      trackEvent,
      projectId: project.id,
      props: {
        numberOfProjects,
        ...getTrackProjectDateProps(project),
        viewMode,
      },
    });
  }

  if (isThumbnailLinkActive) {
    return (
      <ProjectCardContent
        project={project}
        sortBy={sortBy}
        isProjectProcessing={isProjectProcessing}
        thumbnailLinkTo={
          isThumbnailLinkActive ? projectDetailsRoute : undefined
        }
        onThumbnailClick={
          isThumbnailLinkActive ? () => trackOpenDetails("map") : undefined
        }
        shouldSuppressCardHover
      />
    );
  }

  return (
    <Box
      component={Link}
      to={isProjectProcessing ? "#" : projectDetailsRoute}
      onClick={() => !isProjectProcessing && trackOpenDetails("cards")}
      sx={unstyledLinkSx}
    >
      <ProjectCardContent
        project={project}
        sortBy={sortBy}
        isProjectProcessing={isProjectProcessing}
      />
    </Box>
  );
}
